// components/clients/ClientStatement.js
import React, { useEffect } from 'react';
import { Card, Row, Col, Typography, Table, Spin, Alert, Tabs, Tag, Space, DatePicker, Button } from 'antd';
import {
  BankOutlined,
  DollarOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { useClientStatement } from '../../hooks/clientHooks';
import PrintStatementButton from './ClientStatementPrint';
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

const formatCurrency = (amount) => {
  const value = Math.abs(amount).toFixed(2);
  return ` ${amount < 0 ? '-' : ''}${value}`;
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('fr-FR');
};

// Component to render the expanded row details for reglements
const ExpandedReglement = ({ record }) => {
  const { documents } = record;

  return (
    <div className="expanded-details" style={{ padding: '0 20px' }}>
      {documents.factures.length > 0 && (
        <div className="factures-section">
          <Title level={5} style={{ color: '#1890ff', marginTop: '10px' }}>Factures</Title>
          <Table
            dataSource={documents.factures}
            columns={[
              {
                title: 'Reference',
                dataIndex: 'reference',
                key: 'reference',
                render: (text) => <Tag color="blue">{text}</Tag>
              },
              {
                title: 'Original Amount',
                dataIndex: 'originalAmount',
                key: 'originalAmount',
                align: 'right',
                render: (amount) => <Text>{formatCurrency(amount)}</Text>
              },
              {
                title: 'Paid Amount',
                dataIndex: 'paidAmount',
                key: 'paidAmount',
                align: 'right',
                render: (amount) => <Text type="success">{formatCurrency(amount)}</Text>
              },
              {
                title: 'Remaining',
                dataIndex: 'remainingAmount',
                key: 'remainingAmount',
                align: 'right',
                render: (amount) => <Text type={amount > 0 ? "warning" : ""}>{formatCurrency(amount)}</Text>
              }
            ]}
            pagination={false}
            rowKey="reference"
            size="small"
            style={{ marginBottom: '20px' }}
          />
        </div>
      )}

      {documents.avoirs.length > 0 && (
        <div className="avoirs-section">
          <Title level={5} style={{ color: '#52c41a', marginTop: '10px' }}>Avoirs</Title>
          <Table
            dataSource={documents.avoirs}
            columns={[
              {
                title: 'Reference',
                dataIndex: 'reference',
                key: 'reference',
                render: (text) => <Tag color="green">{text}</Tag>
              },
              {
                title: 'Original Amount',
                dataIndex: 'originalAmount',
                key: 'originalAmount',
                align: 'right',
                render: (amount) => <Text>{formatCurrency(amount)}</Text>
              },
              {
                title: 'Paid Amount',
                dataIndex: 'paidAmount',
                key: 'paidAmount',
                align: 'right',
                render: (amount) => <Text>{formatCurrency(amount)}</Text>
              }
            ]}
            pagination={false}
            rowKey="reference"
            size="small"
          />
        </div>
      )}

      {record.hasSurplus && (
        <Alert
          message="Surplus Generated"
          description={`This payment generated a surplus amount.`}
          type="info"
          showIcon
          style={{ marginTop: '20px' }}
        />
      )}
    </div>
  );
};

const ClientStatement = ({ clientId }) => {
    const { 
        statement, 
        loading, 
        error, 
        dateRange, 
        setDateRange,
        handleSearch 
      } = useClientStatement(clientId);



  if (loading) return <Spin size="large" />;
  if (error) return <Alert type="error" message={error} />;
  

  const mainCardStyle = {
    marginBottom: '24px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)'
  };

  const summaryCardStyle = {
    background: '#fafafa',
    borderRadius: '8px'
  };

  return (
    <div style={{ padding: '24px' }}>
        <Card style={mainCardStyle}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={2} style={{ marginBottom: '24px' }}>Relevé de Compte Client</Title>
            </Col>
            <Col>
              <Space>
                <RangePicker
                  value={dateRange}
                  onChange={(dates) => {
                    setDateRange(dates);
                  }}
                  format="DD/MM/YYYY"
                />
                <Button 
                  type="primary" 
                  onClick={handleSearch}
                  disabled={!dateRange[0] || !dateRange[1]}
                >
                  Rechercher
                </Button>
                {statement && <PrintStatementButton statement={statement} dateRange={dateRange} />}
              </Space>
            </Col>
          </Row>
  
          {/* Show a message when no dates are selected */}
          {!statement && !loading && (
            <Alert
              message="Veuillez sélectionner une période"
              description="Sélectionnez une date de début et une date de fin pour afficher le relevé."
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          )}
          
          {statement && (
            <>
        <Row gutter={[24, 24]}>
          <Col span={8}>
            <Card style={summaryCardStyle}>
              <Title level={4}>
                <BankOutlined /> Client Info
              </Title>
              <Paragraph>
                <Text strong>Name: </Text>
                <Text>{statement.clientInfo.name}</Text>
              </Paragraph>
              <Paragraph>
                <Text strong>Account: </Text>
                <Text>{statement.clientInfo.account}</Text>
              </Paragraph>
            </Card>
          </Col>
          
          <Col span={8}>
            <Card style={summaryCardStyle}>
              <Title level={4}>
                <DollarOutlined /> Balance
              </Title>
              <Paragraph>
                <Text strong>Current Balance: </Text>
                <Text type={statement.clientInfo.currentBalance < 0 ? 'danger' : 'success'}>
                  {formatCurrency(statement.clientInfo.currentBalance)}
                </Text>
              </Paragraph>
            </Card>
          </Col>
          
          <Col span={8}>
            <Card style={summaryCardStyle}>
              <Title level={4}>
                <FileTextOutlined /> Summary
              </Title>
              <Row>
                <Col span={24}>
                  <Text strong>Total Règlements: </Text>
                  <Text>{formatCurrency(statement.summary.totalReglements)}</Text>
                </Col>
                <Col span={24}>
                  <Text strong>Available Surplus: </Text>
                  <Text type="success">{formatCurrency(statement.summary.totalSurplus)}</Text>
                </Col>
                <Col span={24}>
                  <Text strong>Unpaid Invoices: </Text>
                  <Text type="warning">{formatCurrency(statement.summary.totalUnpaidFactures)}</Text>
                </Col>
                <Col span={24}>
                  <Text strong>Unused Avoirs: </Text>
                  <Text>{formatCurrency(statement.summary.totalUnusedAvoirs)}</Text>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Tabs defaultActiveKey="1" style={{ marginTop: '24px' }}>
          <TabPane tab="Règlements" key="1">
            <Table
              dataSource={statement.reglements}
              columns={[
                {
                  title: 'Reference',
                  dataIndex: 'reference',
                  key: 'reference',
                  render: (text) => <Tag color="blue">{text}</Tag>
                },
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => formatDate(date)
                },
                {
                  title: 'Payment Method',
                  dataIndex: 'paymentMethod',
                  key: 'paymentMethod',
                  render: (text) => <Tag color="purple">{text}</Tag>
                },
                {
                  title: 'Original Amount',
                  dataIndex: 'originalAmount',
                  key: 'originalAmount',
                  align: 'right',
                  render: (amount) => formatCurrency(amount)
                },
                {
                  title: 'Remaining',
                  dataIndex: 'remainingAmount',
                  key: 'remainingAmount',
                  align: 'right',
                  render: (amount) => <Text type={amount !== 0 ? "warning" : ""}>{formatCurrency(amount)}</Text>
                }
              ]}
              expandable={{
                expandedRowRender: (record) => <ExpandedReglement record={record} />,
                rowExpandable: (record) => 
                  (record.documents.factures.length > 0 || record.documents.avoirs.length > 0),
                expandIcon: ({ expanded, onExpand, record }) =>
                  expanded ? (
                    <DownOutlined rotate={180} onClick={e => onExpand(record, e)} />
                  ) : (
                    <DownOutlined onClick={e => onExpand(record, e)} />
                  )
              }}
              rowKey="reference"
              pagination={false}
            />
          </TabPane>

          <TabPane tab="Surplus" key="2">
            <Table
              dataSource={statement.surplus}
              columns={[
                {
                  title: 'Reference',
                  dataIndex: 'reference',
                  key: 'reference',
                  render: (text) => <Tag color="green">{text}</Tag>
                },
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => formatDate(date)
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right',
                  render: (amount) => <Text type="success">{formatCurrency(amount)}</Text>
                },
                {
                  title: 'Payment Ref',
                  dataIndex: 'reglementRef',
                  key: 'reglementRef',
                  render: (text) => <Tag color="blue">{text}</Tag>
                },
                {
                  title: 'Remarks',
                  dataIndex: 'remarks',
                  key: 'remarks'
                }
              ]}
              rowKey="reference"
              pagination={false}
            />
          </TabPane>

          <TabPane tab="Unpaid Documents" key="3">
            {statement.unpaidDocuments.factures.length > 0 && (
              <>
                <Title level={4}>Unpaid Factures</Title>
                <Table
                  dataSource={statement.unpaidDocuments.factures}
                  columns={[
                    {
                      title: 'Reference',
                      dataIndex: 'reference',
                      key: 'reference',
                      render: (text) => <Tag color="blue">{text}</Tag>
                    },
                    {
                      title: 'Date',
                      dataIndex: 'date',
                      key: 'date',
                      render: (date) => formatDate(date)
                    },
                    {
                      title: 'Total Amount',
                      dataIndex: 'totalAmount',
                      key: 'totalAmount',
                      align: 'right',
                      render: (amount) => formatCurrency(amount)
                    },
                    {
                      title: 'Paid Amount',
                      dataIndex: 'paidAmount',
                      key: 'paidAmount',
                      align: 'right',
                      render: (amount) => <Text type="success">{formatCurrency(amount)}</Text>
                    },
                    {
                      title: 'Remaining',
                      dataIndex: 'remainingAmount',
                      key: 'remainingAmount',
                      align: 'right',
                      render: (amount) => <Text type="warning">{formatCurrency(amount)}</Text>
                    }
                  ]}
                  rowKey="reference"
                  pagination={false}
                />
              </>
            )}

            {statement.unpaidDocuments.avoirs.length > 0 && (
              <>
                <Title level={4} style={{ marginTop: '24px' }}>Unpaid Avoirs</Title>
                <Table
                  dataSource={statement.unpaidDocuments.avoirs}
                  columns={[
                    {
                      title: 'Reference',
                      dataIndex: 'reference',
                      key: 'reference',
                      render: (text) => <Tag color="green">{text}</Tag>
                    },
                    {
                      title: 'Date',
                      dataIndex: 'date',
                      key: 'date',
                      render: (date) => formatDate(date)
                    },
                    {
                      title: 'Amount',
                      dataIndex: 'amount',
                      key: 'amount',
                      align: 'right',
                      render: (amount) => formatCurrency(amount)
                    }
                  ]}
                  rowKey="reference"
                  pagination={false}
                />
              </>
            )}
          </TabPane>
        </Tabs>
        </>
    )}
      </Card>
    </div>
  );
};

export default ClientStatement;